import React, { useEffect, useState } from "react";
import { Link, useLocation, useNavigate, useParams } from "react-router-dom";
import { CalendarWrapperComponent } from "./CalendarWrapperComponent";
import { CalendarFilterI, getLogbookDates } from "./constants";
import { ApolloError, useReactiveVar } from "@apollo/client";
import ErrorService from "../../services/ErrorService";
import { ErrorModalComponent } from "@me-pos/error-modal";
import i18next from "i18next";
import EmptyList from "@services/ui-components/src/EmptyList/EmptyList";
import {
    useGetBookingByIdQuery,
    useGetBookingsListQuery,
    useGetEmployeesNotInBranchForLogbookQuery,
    useGetTimeZoneForLogbookQuery,
} from "@me-team/host/main/logbook/graphql/logbook.hooks";
import { AddAccessRightsModal, AddEmployeeFromAnotherBranchModal, Spinner } from "@services/ui-components";
import { currentCompanyIdVar } from "@me-team/host/main/globalVar/state";
import { useCreateUpdateUserBranchRelationMutation } from "@me-team/host/main/setUpAccessRole/graphql/setUpAccessRole.hooks";
import { EmployeeProxy, UserBranchRelationInput } from "@me-team/host/main/graphql/types";
import { FormDataAccessRights } from "@services/ui-components/src/Modal/EmployeeAccessToBranchModal/types";
import { SimpleModal } from "@services/ui-components/src/SimpleModal/SimpleModal";
import { useTranslation } from "react-i18next";
import { Col, Container, Row } from "react-bootstrap";
import { DeepPartial } from "../../interfaces/interfaces";
import { LogbookAccess } from "./buildLogbookCalendarAccess";
import { CompanyAccess } from "@me-team/host/main/graphql/types";
import { useRoleAccess } from "@me-team/host/src/utils/useRoleAccess";
import { useModulesAccess } from "@me-team/host/src/utils/useModulesAccess";
import { AllModulesTagName, bookingStatusIds } from "../../utils/enums";
import type { BookingStatusChangedPayload } from "@me-pos/calendar";

type WriteOffTarget = {
    companyId: number;
    branchId: number;
    bookingUuid: string;
};

type WriteOffReadyBookingStatusChangedPayload = BookingStatusChangedPayload & {
    companyId: number;
    bookingUuid: string;
};

const shouldOpenWriteOffModalAfterStatusChange = (
    payload: BookingStatusChangedPayload,
    isWithWarehouse: boolean,
    canWriteOffLogbookConsumables: boolean
): payload is WriteOffReadyBookingStatusChangedPayload =>
    payload.previousStatusId !== bookingStatusIds.COMPLETED &&
    payload.statusId === bookingStatusIds.COMPLETED &&
    isWithWarehouse &&
    !payload.isWriteOffCompleted &&
    Boolean(payload.bookingUuid) &&
    Boolean(payload.companyId) &&
    Boolean(payload.branchId) &&
    canWriteOffLogbookConsumables;

type CalendarWrapperProps = {
    filters: CalendarFilterI;
    isShowAddEmployeeFromAnotherBranchModal: boolean;
    onShowAddEmployeeFromAnotherBranchModal: () => void;
    setNotificationOfMissingData: React.Dispatch<React.SetStateAction<string>>;
    notificationOfMissingData: string;
    refetchEmployeeList?: boolean;
    handleRefetchEmployeeList?: () => void;
    shouldShowOnlyOwnLogbook: boolean;
    logbookAccess: LogbookAccess;
    keySchedule?: number;
};

const CalendarWrapper: React.FC<CalendarWrapperProps> = ({
    filters,
    isShowAddEmployeeFromAnotherBranchModal,
    onShowAddEmployeeFromAnotherBranchModal,
    setNotificationOfMissingData,
    notificationOfMissingData,
    refetchEmployeeList,
    handleRefetchEmployeeList,
    shouldShowOnlyOwnLogbook,
    logbookAccess,
    keySchedule,
}) => {
    const { branchId } = useParams();
    const location = useLocation();
    const navigate = useNavigate();
    const { t: translate } = useTranslation();
    const currentCompanyId = useReactiveVar(currentCompanyIdVar);
    const canWriteOffLogbookConsumables = useRoleAccess(CompanyAccess.LogbookConsumablesWriteOff);
    const isWithWarehouse = useModulesAccess([AllModulesTagName.WAREHOUSES]);
    const [showPreloader, setShowPreloader] = useState<boolean>(false);
    const [isErrorModalOpen, setIsErrorModalOpen] = useState<number>(null);
    const getDates = branchId && getLogbookDates(branchId.toString());
    const newBookingId: number = location.state?.recordId;
    const [isNeedToWriteOff, setIsNeedToWriteOff] = useState<boolean>(location?.state?.isNeedToWriteOff);
    const [writeOffTarget, setWriteOffTarget] = useState<WriteOffTarget | null>(null);
    const [showAddAccessRightsModal, setShowAddAccessRightsModal] = useState(false);
    const [selectedEmployeeToAdd, setSelectedEmployeeToAdd] = useState(null);
    const [employeesWithoutAccessCurrentBranch, setEmployeesWithoutAccessCurrentBranch] = useState<
        DeepPartial<EmployeeProxy>[]
    >([]);
    const [delayTime, setDelayTime] = useState(false);
    const handleToggleAddAccessRightsModal = () => setShowAddAccessRightsModal((prevState) => !prevState);
    const shouldShowWriteOffModal = Boolean(
        (isNeedToWriteOff || (writeOffTarget && isWithWarehouse)) && canWriteOffLogbookConsumables
    );
    const writeOffLink = writeOffTarget
        ? `/products/write-off/company/${writeOffTarget.companyId}/branch/${writeOffTarget.branchId}/bookingUuid/${writeOffTarget.bookingUuid}`
        : `/products/write-off/company/${currentCompanyId}/branch/${branchId}/bookingUuid/${location?.state?.recordUuid}`;
    const handleBookingStatusChanged = React.useCallback(
        (payload: BookingStatusChangedPayload) => {
            if (!shouldOpenWriteOffModalAfterStatusChange(payload, isWithWarehouse, canWriteOffLogbookConsumables)) {
                return;
            }

            setWriteOffTarget({
                companyId: payload.companyId,
                branchId: payload.branchId,
                bookingUuid: payload.bookingUuid,
            });
        },
        [canWriteOffLogbookConsumables, isWithWarehouse]
    );
    const [createUserBranchRelation] = useCreateUpdateUserBranchRelationMutation({
        context: {
            errorType: "local",
        },
        onError: (error: ApolloError) => {
            setIsErrorModalOpen(ErrorService.errorHandling(error));
        },
    });

    const { data: employeesNotInBranchData, refetch: refetchEmployeesNotInBranch } =
        useGetEmployeesNotInBranchForLogbookQuery({
            variables: {
                companyId: +currentCompanyId,
                excludeBranchId: +branchId,
            },
            context: { errorType: "local" },
            skip: !currentCompanyId || !branchId,
            onError: (error) => {
                setIsErrorModalOpen(ErrorService.errorHandling(error));
            },
        });

    useEffect(() => {
        employeesNotInBranchData &&
            setEmployeesWithoutAccessCurrentBranch(employeesNotInBranchData?.user.company.employeesNotInBranch);
    }, [employeesNotInBranchData]);

    useEffect(() => {
        if (!location.state?.isNeedToWriteOff) return;

        const nextState = { ...location.state };
        delete nextState.isNeedToWriteOff;

        navigate(location.pathname + location.search, {
            replace: true,
            state: nextState,
        });
    }, [location.pathname, location.search, location.state, navigate]);

    const { data: infoCompanyData, loading } = useGetTimeZoneForLogbookQuery({
        variables: { companyId: +currentCompanyId },
        skip: !currentCompanyId,
        context: {
            errorType: "local",
        },
        fetchPolicy: "network-only",
        onError: (error: ApolloError) => {
            setIsErrorModalOpen(ErrorService.errorHandling(error));
        },
    });

    const { data: bookingData } = useGetBookingByIdQuery({
        skip: !newBookingId || !currentCompanyId || !branchId,
        variables: {
            branchId: +branchId,
            bookingId: newBookingId && newBookingId,
            companyId: +currentCompanyId,
        },
        context: {
            errorType: "local",
        },
        onError: (error: ApolloError) => {
            setIsErrorModalOpen(ErrorService.errorHandling(error));
        },
    });
    const newDate: string = bookingData?.user?.company?.branches?.[0].bookings?.bookings?.[0].date;
    const timeZone: string = infoCompanyData?.user?.company?.timeZone?.name;

    const { loading: loadingBookingList } = useGetBookingsListQuery({
        variables: {
            branchId: +branchId,
            isDeleted: false,
            startDate: getDates?.startDate && getDates?.startDate,
            endDate: getDates?.endDate && getDates?.endDate,
            statusId: filters?.statusId && filters?.statusId,
            serviceId: filters?.serviceId && filters?.serviceId,
            employeeId: filters?.employeeId && filters?.employeeId,
            positionId: filters?.positionId && filters?.positionId,
            companyId: +currentCompanyId,
        },

        skip:
            shouldShowOnlyOwnLogbook ||
            (!filters?.statusId && !filters?.serviceId && !filters?.employeeId && !filters?.positionId) ||
            !currentCompanyId ||
            !branchId,
        onCompleted: (data) => {
            const bookingsList = data?.user?.company?.branches?.[0]?.bookingsList || [];
            const employeesList = data?.user?.company?.employeesList || [];
            const employees: Partial<EmployeeProxy>[] = data?.user?.company?.employees?.employees;
            const tariffBlockingEmployee = !!employees?.[0]?.tariffBlocking;
            if ((filters.statusId || filters.serviceId) && bookingsList.length === 0) {
                setNotificationOfMissingData("No records");
            } else if (
                (filters.positionId && employeesList.length === 0) ||
                (filters.employeeId && tariffBlockingEmployee)
            ) {
                setNotificationOfMissingData("No data for the request");
            } else {
                setNotificationOfMissingData("");
            }
        },
        context: {
            errorType: "local",
        },
        onError: (error: ApolloError) => {
            setIsErrorModalOpen(ErrorService.errorHandling(error));
        },
    });

    const showPreloaderHandler = () => {
        setShowPreloader(true);
    };
    const hidePreloaderHandler = () => {
        setShowPreloader(false);
    };

    const handleAddAccessRightsNewEmployee = async (values: FormDataAccessRights) => {
        const input: UserBranchRelationInput = {
            branchId: values.branchId,
            employeeId: values.employeeId,
            roleId: values.roleId,
        };
        currentCompanyId &&
            branchId &&
            (await createUserBranchRelation({
                variables: {
                    companyId: +currentCompanyId,
                    input: [input],
                    branchId: +branchId,
                },
                onCompleted: (data) => {
                    data && refetchEmployeesNotInBranch();
                    data && handleRefetchEmployeeList();
                },
                context: {
                    errorType: "local",
                },
                onError: (error: ApolloError) => {
                    setIsErrorModalOpen(ErrorService.errorHandling(error));
                },
            }));
        handleToggleAddAccessRightsModal();
    };

    const serializedFilters = JSON.stringify(filters);

    const secondComponent = React.useMemo(
        () => (
            <CalendarWrapperComponent
                currentCompanyId={+currentCompanyId}
                currentBranchId={+branchId}
                filters={filters}
                timeZone={timeZone}
                showPreloaderHandler={showPreloaderHandler}
                hidePreloaderHandler={hidePreloaderHandler}
                newBookingId={newBookingId}
                newDate={newDate}
                refetchEmployeeList={refetchEmployeeList}
                logbookAccess={logbookAccess}
                isWithWarehouse={isWithWarehouse}
                onBookingStatusChanged={handleBookingStatusChanged}
            />
        ),
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [
            currentCompanyId,
            branchId,
            serializedFilters,
            newBookingId,
            timeZone,
            newDate,
            refetchEmployeeList,
            logbookAccess,
            isWithWarehouse,
            handleBookingStatusChanged,
        ]
    );

    const bookingLoadDelay = newBookingId ? 1000 : 0;
    // Задержка перед рендером календаря, после создания или редактирования букинга, чтоб данные
    // в календаре успевали прийти полученые в самом календаре
    useEffect(() => {
        const timer = setTimeout(() => {
            setDelayTime(true);
        }, bookingLoadDelay);
        return () => clearTimeout(timer);
    }, [bookingLoadDelay]);

    if (
        loading ||
        (loadingBookingList &&
            !shouldShowOnlyOwnLogbook &&
            (filters?.statusId || filters?.serviceId || filters?.employeeId || filters?.positionId)) ||
        !branchId ||
        !currentCompanyId
    ) {
        return <Spinner />;
    }

    return (
        <div key={keySchedule}>
            {showPreloader && (
                <div className="d-flex all-unset align-items-center justify-content-center position-fixed top-50 end-50 z-3">
                    <div className="preloader__spinner"></div>
                </div>
            )}
            {!shouldShowOnlyOwnLogbook &&
            (filters.serviceId || filters.statusId || filters.positionId || filters.employeeId) &&
            notificationOfMissingData ? (
                <EmptyList title={notificationOfMissingData} customClass="mb-5" />
            ) : (
                delayTime && secondComponent
            )}

            <AddEmployeeFromAnotherBranchModal
                isShowAddEmployeeFromAnotherBranchModal={isShowAddEmployeeFromAnotherBranchModal}
                onCloseAddEmployeeFromAnotherBranch={onShowAddEmployeeFromAnotherBranchModal}
                employeesWithoutAccessCurrentBranch={employeesWithoutAccessCurrentBranch}
                onSelectedEmployeeToAdd={setSelectedEmployeeToAdd}
                toggleAddAccessRightsModal={handleToggleAddAccessRightsModal}
            />
            <AddAccessRightsModal
                isAddAccessRightsModal={showAddAccessRightsModal}
                onCloseAddAccessRightsModal={handleToggleAddAccessRightsModal}
                selectedEmployeeToAdd={selectedEmployeeToAdd}
                onAddAccessRightsEmployee={handleAddAccessRightsNewEmployee}
            />

            {isErrorModalOpen ? (
                <ErrorModalComponent
                    i18n={i18next}
                    onClose={() => {
                        setIsErrorModalOpen(null);
                    }}
                    isOpen={!!isErrorModalOpen}
                    currentError={isErrorModalOpen}
                />
            ) : null}
            <SimpleModal
                isOpen={shouldShowWriteOffModal}
                onClose={() => {
                    setIsNeedToWriteOff(false);
                    setWriteOffTarget(null);
                }}
                title={translate("This Service is Completed")}
                description={translate("Please write off the materials used during the work from the warehouse.")}
            >
                <Container>
                    <Row>
                        <Col xs={12} className={"pe-0"}>
                            <Link to={writeOffLink} className={"btn btn-primary w-100 fw-normal"}>
                                {translate("To be written off")}
                            </Link>
                        </Col>
                    </Row>
                </Container>
            </SimpleModal>
        </div>
    );
};

export default CalendarWrapper;
